import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { registerDialogApi } from "../utils/browserDialogs";

const AppDialogContext = createContext(null);

export function AppDialogProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [promptValue, setPromptValue] = useState("");
  const promptValueRef = useRef("");
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const restoreFocusRef = useRef(false);

  const close = useCallback(() => {
    restoreFocusRef.current = true;
    setDialog(null);
    setPromptValue("");
    promptValueRef.current = "";
  }, []);

  const alert = useCallback((options) => {
    return new Promise((resolve) => {
      previouslyFocusedRef.current = document.activeElement;
      setDialog({
        type: "alert",
        title: options?.title || "Notice",
        message: options?.message || options || "",
        confirmText: options?.confirmText || "OK",
        variant: options?.variant || "default",
        onConfirm: () => {
          close();
          resolve(true);
        },
      });
    });
  }, [close]);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      previouslyFocusedRef.current = document.activeElement;
      setDialog({
        type: "confirm",
        title: options?.title || "Confirm action",
        message: options?.message || "",
        confirmText: options?.confirmText || "Confirm",
        cancelText: options?.cancelText || "Cancel",
        variant: options?.variant || "danger",
        onCancel: () => {
          close();
          resolve(false);
        },
        onConfirm: () => {
          close();
          resolve(true);
        },
      });
    });
  }, [close]);

  const prompt = useCallback((options) => {
    return new Promise((resolve) => {
      const defaultValue = options?.defaultValue || "";

      previouslyFocusedRef.current = document.activeElement;
      promptValueRef.current = defaultValue;
      setPromptValue(defaultValue);

      setDialog({
        type: "prompt",
        title: options?.title || "Input required",
        message: options?.message || "",
        label: options?.label || "Value",
        placeholder: options?.placeholder || "",
        confirmText: options?.confirmText || "Continue",
        cancelText: options?.cancelText || "Cancel",
        variant: options?.variant || "default",
        required: options?.required !== false,
        onCancel: () => {
          close();
          resolve(null);
        },
        onConfirm: () => {
          const value = String(promptValueRef.current || "").trim();

          if (options?.required !== false && !value) {
            return;
          }

          close();
          resolve(value);
        },
      });
    });
  }, [close]);

  const select = useCallback((options) => {
    return new Promise((resolve) => {
      const choices = Array.isArray(options?.options) ? options.options : [];
      const allowedValues = choices.map((choice) => String(choice.value));
      const defaultValue = allowedValues.includes(String(options?.defaultValue))
        ? String(options.defaultValue)
        : (allowedValues[0] || "");

      previouslyFocusedRef.current = document.activeElement;
      promptValueRef.current = defaultValue;
      setPromptValue(defaultValue);

      setDialog({
        type: "select",
        title: options?.title || "Select an option",
        message: options?.message || "",
        label: options?.label || "Selection",
        options: choices,
        confirmText: options?.confirmText || "Continue",
        cancelText: options?.cancelText || "Cancel",
        variant: options?.variant || "default",
        onCancel: () => {
          close();
          resolve(null);
        },
        onConfirm: () => {
          const value = String(promptValueRef.current || "");
          if (!allowedValues.includes(value)) return;
          close();
          resolve(value);
        },
      });
    });
  }, [close]);

  const handlePromptChange = (e) => {
    promptValueRef.current = e.target.value;
    setPromptValue(e.target.value);
  };

  const value = useMemo(() => ({ alert, confirm, prompt, select }), [alert, confirm, prompt, select]);

  useEffect(() => {
    registerDialogApi({ alert, confirm, prompt, select });
  }, [alert, confirm, prompt, select]);

  useLayoutEffect(() => {
    if (dialog || !restoreFocusRef.current) return;

    restoreFocusRef.current = false;
    const opener = previouslyFocusedRef.current;
    previouslyFocusedRef.current = null;

    if (opener?.isConnected) {
      opener.focus();
    }
  }, [dialog]);

  useEffect(() => {
    if (!dialog) return undefined;

    const container = dialogRef.current;

    function onKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        (dialog.onCancel || dialog.onConfirm)();
        return;
      }

      if (event.key !== "Tab" || !container) return;
      const focusable = [...container.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [dialog]);

  return (
    <AppDialogContext.Provider value={value}>
      {children}

      {dialog && (
        <div className="app-dialog-backdrop" role="presentation">
          <div
            ref={dialogRef}
            className={`app-dialog app-dialog-${dialog.variant}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="app-dialog-title"
            aria-describedby={dialog.message ? "app-dialog-description" : undefined}
          >
            <div className="app-dialog-icon">
              {dialog.variant === "danger" ? "!" : "i"}
            </div>

            <div className="app-dialog-content">
              <h3 id="app-dialog-title">{dialog.title}</h3>

              {dialog.message && <p id="app-dialog-description">{dialog.message}</p>}

              {dialog.type === "prompt" && (
                <div className="mt-3">
                  <label className="form-label" htmlFor="app-dialog-input">{dialog.label}</label>
                  <input
                    id="app-dialog-input"
                    className="form-control form-control-lg"
                    value={promptValue}
                    placeholder={dialog.placeholder}
                    onChange={handlePromptChange}
                    autoFocus
                    required={dialog.required}
                  />
                </div>
              )}

              {dialog.type === "select" && (
                <div className="mt-3">
                  <label className="form-label" htmlFor="app-dialog-select">{dialog.label}</label>
                  <select
                    id="app-dialog-select"
                    className="form-select form-select-lg"
                    value={promptValue}
                    onChange={handlePromptChange}
                    autoFocus
                  >
                    {dialog.options.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="app-dialog-actions">
                {(dialog.type === "confirm" || dialog.type === "prompt" || dialog.type === "select") && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={dialog.onCancel}
                  >
                    {dialog.cancelText}
                  </button>
                )}

                <button
                  type="button"
                  className={dialog.variant === "danger" ? "btn btn-danger" : "btn btn-dark"}
                  onClick={dialog.onConfirm}
                  autoFocus={dialog.type !== "prompt" && dialog.type !== "select"}
                >
                  {dialog.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppDialogContext.Provider>
  );
}

export function useAppDialog() {
  const context = useContext(AppDialogContext);

  if (!context) {
    throw new Error("useAppDialog must be used inside AppDialogProvider");
  }

  return context;
}
